import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckSquare, X, Loader2 } from "lucide-react";
import { listRDOs, updateRDO } from "@/services/rdoStorageSupabase";
import type { RDO } from "@/services/rdoStorageService";
import { useUserRole } from "@/hooks/useUserRole";
import { useToast } from "@/hooks/use-toast";
import { getCurrentUserName } from "@/utils/userUtils";

interface WorkApproveRDOsProps {
  obraId: string;
  obraName: string;
  isVisible?: boolean;
  onClose?: () => void;
}

export function WorkApproveRDOs({ obraId, obraName, isVisible = true, onClose }: WorkApproveRDOsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rdos, setRdos] = useState<RDO[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const { userRole, canViewDocuments } = useUserRole();
  const { toast } = useToast();
  
  // Check if user can approve RDOs (admin or user role)
  const canApprove = userRole === 'admin' || userRole === 'user';

  // Carrega no mount para o contador do card refletir a realidade sem que o
  // usuário precise abrir o modal; recarrega ao abrir (dados frescos).
  useEffect(() => {
    loadRDOs();
  }, [obraId]);

  useEffect(() => {
    if (isModalOpen) {
      loadRDOs();
    }
  }, [isModalOpen]);

  const loadRDOs = async () => {
    setLoading(true);
    try {
      const allRdos = await listRDOs(obraId);
      // Filter for pending approval states
      const pendingRdos = allRdos.filter(rdo => 
        rdo.status === 'in_review' || 
        rdo.status === 'draft' ||
        (rdo.approved === false || rdo.approved === undefined)
      );
      setRdos(pendingRdos);
    } catch (error) {
      console.error('Error loading RDOs:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os RDOs pendentes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (rdoId: string) => {
    if (!canApprove) {
      toast({
        title: "Permissão negada",
        description: "Você não tem permissão para aprovar este RDO.",
        variant: "destructive",
      });
      return;
    }

    setActionLoading(rdoId);
    try {
      const result = await updateRDO(rdoId, {
        status: 'approved',
        approved: true,
        approvedBy: getCurrentUserName(),
        approvedAt: new Date().toISOString(),
      });

      if (result) {
        toast({
          title: "RDO Aprovado",
          description: "O RDO foi aprovado com sucesso.",
        });
        
        // Remove from list and reload
        loadRDOs();
        
        // Analytics event
        console.log('Analytics: obra_rdo_approve_success', { rdoId, obraId });
      } else {
        throw new Error('Failed to update RDO');
      }
    } catch (error) {
      console.error('Error approving RDO:', error);
      toast({
        title: "Erro",
        description: "Não foi possível aprovar o RDO. Tente novamente.",
        variant: "destructive",
      });
      
      // Analytics event
      console.log('Analytics: obra_rdo_approve_error', { rdoId, obraId, error });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReview = async (rdoId: string) => {
    setActionLoading(rdoId);
    try {
      const result = await updateRDO(rdoId, {
        status: 'in_review',
        approved: false,
      });

      if (result) {
        toast({
          title: "RDO em Revisão",
          description: "O RDO foi marcado para revisão.",
        });
        
        loadRDOs();
        
        // Analytics event
        console.log('Analytics: obra_rdo_click_review', { rdoId, obraId });
      } else {
        throw new Error('Failed to update RDO');
      }
    } catch (error) {
      console.error('Error reviewing RDO:', error);
      toast({
        title: "Erro",
        description: "Não foi possível marcar o RDO para revisão.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    // Analytics event
    console.log('Analytics: obra_rdo_open_popup', { obraId });
  };

  if (!isVisible) return null;

  const pendingCount = rdos.length;
  const statusMapping = {
    'draft': 'Rascunho',
    'in_review': 'Em Revisão',
    'signed': 'Assinado',
    'approved': 'Aprovado'
  };

  const getStatusBadge = (status: RDO['status']) => {
    const statusText = statusMapping[status || 'draft'] || 'Pendente';
    const className = status === 'approved' 
      ? "bg-success/10 text-success"
      : status === 'in_review'
      ? "bg-primary/10 text-primary"
      : status === 'signed'
      ? "bg-info/10 text-info"
      : "bg-warning/10 text-warning";
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        {statusText}
      </span>
    );
  };

  return (
    <>
      <Card 
        className="shadow-card hover:shadow-card-hover transition-shadow cursor-pointer relative group"
        onClick={handleOpenModal}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-muted z-10"
          aria-label="Fechar Aprovar RDOs"
        >
          <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
        </button>
        
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Aprovar RDOs
            <div className="text-xs font-normal text-muted-foreground/70 mt-0.5">
              {pendingCount} pendentes
            </div>
          </CardTitle>
          <div className="p-2 rounded-lg bg-warning/10 flex-shrink-0">
            <CheckSquare className="w-4 h-4 text-warning" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">{pendingCount}</div>
        </CardContent>
      </Card>

      {/* RDO Approval Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>RDOs Pendentes de Aprovação - {obraName}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Carregando RDOs...</span>
              </div>
            ) : rdos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Não há RDOs pendentes desta obra</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número RDO</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Última Atualização</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rdos.map((rdo) => (
                      <TableRow key={rdo.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell className="font-medium">
                          {`RDO-${rdo.id.split('_')[1]?.slice(-4) || '0001'}`}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(rdo.status)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(rdo.updatedAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(rdo.date).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <Button 
                                      size="sm" 
                                      variant="default"
                                      disabled={!canApprove || actionLoading === rdo.id}
                                      onClick={() => handleApprove(rdo.id)}
                                    >
                                      {actionLoading === rdo.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        'Aprovar'
                                      )}
                                    </Button>
                                  </div>
                                </TooltipTrigger>
                                {!canApprove && (
                                  <TooltipContent>
                                    <p>Você não tem permissão para aprovar este RDO</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                            
                            <Button 
                              size="sm" 
                              variant="outline"
                              disabled={actionLoading === rdo.id}
                              onClick={() => handleReview(rdo.id)}
                            >
                              {actionLoading === rdo.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Revisar'
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}